import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { useWaiterAuth } from '../context/WaiterAuthContext';
import { useOutlet } from '../../context/OutletContext';
import { useTerminal, TERMINAL_STATUS } from '../context/TerminalContext';
import WaiterSidebar from '../components/WaiterSidebar';
import './MasterPOSDashboard.css';

const MasterPOSDashboard = () => {
  const { currentWaiter } = useWaiterAuth();
  const { selectedOutlet } = useOutlet();
  const { terminals, currentTerminal } = useTerminal();

  const [allOrders, setAllOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [terminalOrders, setTerminalOrders] = useState({});

  // Subscribe to all orders for this outlet
  useEffect(() => {
    if (!selectedOutlet) return;

    const q = query(
      collection(db, 'orders'),
      where('outletId', '==', selectedOutlet.id),
      where('orderType', '==', 'dine-in'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setAllOrders(ordersData);

      // Group orders by terminal
      const grouped = {};
      ordersData.forEach(order => {
        const terminalId = order.terminalId || 'unassigned';
        if (!grouped[terminalId]) {
          grouped[terminalId] = [];
        }
        grouped[terminalId].push(order);
      });

      setTerminalOrders(grouped);
    });

    return () => unsubscribe();
  }, [selectedOutlet]);

  // Get active billing terminals
  const getActiveBillingTerminals = () => {
    return terminals.filter(t =>
      t.status === TERMINAL_STATUS.ACTIVE &&
      (t.type === 'billing' || t.type === 'master')
    );
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      'pending': { text: 'Pending', color: '#FF9800', bg: '#FFF3E0' },
      'confirmed': { text: 'In Kitchen', color: '#4CAF50', bg: '#E8F5E9' },
      'preparing': { text: 'Preparing', color: '#4CAF50', bg: '#E8F5E9' },
      'ready': { text: 'Ready', color: '#9C27B0', bg: '#F3E5F5' },
      'delivered': { text: 'Served', color: '#2196F3', bg: '#E3F2FD' },
      'completed': { text: 'Completed', color: '#607D8B', bg: '#ECEFF1' }
    };
    return badges[status] || { text: status, color: '#9E9E9E', bg: '#F5F5F5' };
  };

  // Calculate terminal total
  const getTerminalTotal = (terminalId) => {
    const orders = terminalOrders[terminalId] || [];
    const activeOrders = orders.filter(o => o.status !== 'completed' && o.status !== 'cancelled');
    return activeOrders.reduce((sum, order) => sum + (order.total || 0), 0);
  };

  // Get time ago
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  // Filter orders by status
  const getFilteredOrders = () => {
    if (activeTab === 'all') return allOrders;
    if (activeTab === 'pending') return allOrders.filter(o => o.status === 'pending');
    if (activeTab === 'in-kitchen') return allOrders.filter(o => o.status === 'confirmed' || o.status === 'preparing');
    if (activeTab === 'ready') return allOrders.filter(o => o.status === 'ready');
    if (activeTab === 'completed') return allOrders.filter(o => o.status === 'completed' || o.status === 'delivered');
    return allOrders;
  };

  const activeBillingTerminals = getActiveBillingTerminals();
  const filteredOrders = getFilteredOrders();

  // Counts
  const counts = {
    all: allOrders.length,
    pending: allOrders.filter(o => o.status === 'pending').length,
    inKitchen: allOrders.filter(o => o.status === 'confirmed' || o.status === 'preparing').length,
    ready: allOrders.filter(o => o.status === 'ready').length,
    completed: allOrders.filter(o => o.status === 'completed' || o.status === 'delivered').length
  };

  if (!selectedOutlet) {
    return (
      <div className="master-pos-container">
        <WaiterSidebar currentWaiter={currentWaiter} />
        <div className="master-pos-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-pos-container">
      <WaiterSidebar currentWaiter={currentWaiter} />

      <div className="master-pos-main">
        {/* Header */}
        <div className="master-pos-header">
          <div className="header-left">
            <h1>👑 Master POS Station</h1>
            <p>Monitor and control all billing counters</p>
          </div>
          <div className="header-right">
            <div className="outlet-badge">📍 {selectedOutlet.name}</div>
            {currentTerminal && (
              <div className="current-terminal-badge">
                🖥️ {currentTerminal.terminalNumber}
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card active">
            <div className="stat-icon">🖥️</div>
            <div className="stat-content">
              <div className="stat-value">{activeBillingTerminals.length}</div>
              <div className="stat-label">Active Terminals</div>
            </div>
          </div>
          <div className="stat-card pending">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <div className="stat-value">{counts.pending}</div>
              <div className="stat-label">Pending Orders</div>
            </div>
          </div>
          <div className="stat-card kitchen">
            <div className="stat-icon">🍳</div>
            <div className="stat-content">
              <div className="stat-value">{counts.inKitchen}</div>
              <div className="stat-label">In Kitchen</div>
            </div>
          </div>
          <div className="stat-card ready">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-value">{counts.ready}</div>
              <div className="stat-label">Ready to Serve</div>
            </div>
          </div>
        </div>

        {/* Terminals Grid - Petpooja Style */}
        <div className="terminals-section">
          <h2>📡 Active Billing Counters</h2>
          <div className="terminals-bills-grid">
            {activeBillingTerminals.map((terminal, index) => {
              const orders = terminalOrders[terminal.id] || [];
              const activeOrders = orders.filter(o =>
                o.status !== 'completed' && o.status !== 'cancelled'
              );
              const terminalTotal = getTerminalTotal(terminal.id);

              return (
                <div key={terminal.id} className="terminal-bill-card">
                  <div className="bill-card-header">
                    <div className="bill-number">Bill {index + 1}</div>
                    <div className="terminal-info">
                      <span className="terminal-name">{terminal.name}</span>
                      <span className="terminal-num">{terminal.terminalNumber}</span>
                    </div>
                  </div>

                  <div className="bill-card-body">
                    {activeOrders.length > 0 ? (
                      <>
                        <div className="bill-summary">
                          <div className="summary-item">
                            <span className="label">Active Orders</span>
                            <span className="value">{activeOrders.length}</span>
                          </div>
                          <div className="summary-item">
                            <span className="label">Total Amount</span>
                            <span className="value">₹{terminalTotal.toFixed(2)}</span>
                          </div>
                        </div>

                        <div className="orders-list-compact">
                          {activeOrders.slice(0, 3).map(order => {
                            const badge = getStatusBadge(order.status);
                            return (
                              <div key={order.id} className="order-item-compact">
                                <div className="order-item-left">
                                  <span className="order-num">{order.orderNumber}</span>
                                  <span className="table-num">T{order.tableNumber}</span>
                                </div>
                                <div className="order-item-right">
                                  <span className="order-amount">₹{order.total?.toFixed(2)}</span>
                                  <span
                                    className="order-status-badge"
                                    style={{ backgroundColor: badge.bg, color: badge.color }}
                                  >
                                    {badge.text}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                          {activeOrders.length > 3 && (
                            <div className="more-orders">
                              +{activeOrders.length - 3} more orders
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="no-orders-in-terminal">
                        <span className="no-orders-icon">💤</span>
                        <p>No active orders</p>
                      </div>
                    )}
                  </div>

                  <div className="bill-card-footer">
                    <button className="btn-view-terminal">View Details</button>
                  </div>
                </div>
              );
            })}

            {activeBillingTerminals.length === 0 && (
              <div className="no-terminals-message">
                <h3>No active terminals</h3>
                <p>Please activate billing counters from Terminal Management</p>
              </div>
            )}
          </div>
        </div>

        {/* All Orders Section */}
        <div className="all-orders-section">
          <div className="orders-header">
            <h2>All Orders</h2>
            <div className="orders-tabs">
              <button
                className={`orders-tab ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                All <span className="tab-count">{counts.all}</span>
              </button>
              <button
                className={`orders-tab ${activeTab === 'pending' ? 'active' : ''}`}
                onClick={() => setActiveTab('pending')}
              >
                Pending <span className="tab-count">{counts.pending}</span>
              </button>
              <button
                className={`orders-tab ${activeTab === 'in-kitchen' ? 'active' : ''}`}
                onClick={() => setActiveTab('in-kitchen')}
              >
                In Kitchen <span className="tab-count">{counts.inKitchen}</span>
              </button>
              <button
                className={`orders-tab ${activeTab === 'ready' ? 'active' : ''}`}
                onClick={() => setActiveTab('ready')}
              >
                Ready <span className="tab-count">{counts.ready}</span>
              </button>
              <button
                className={`orders-tab ${activeTab === 'completed' ? 'active' : ''}`}
                onClick={() => setActiveTab('completed')}
              >
                Completed <span className="tab-count">{counts.completed}</span>
              </button>
            </div>
          </div>

          <div className="orders-table">
            <div className="table-header">
              <div className="col">Order #</div>
              <div className="col">Table</div>
              <div className="col">Terminal</div>
              <div className="col">Items</div>
              <div className="col">Amount</div>
              <div className="col">Status</div>
              <div className="col">Time</div>
            </div>
            <div className="table-body">
              {filteredOrders.map(order => {
                const badge = getStatusBadge(order.status);
                const terminal = terminals.find(t => t.id === order.terminalId);

                return (
                  <div key={order.id} className="table-row">
                    <div className="col">{order.orderNumber}</div>
                    <div className="col">Table {order.tableNumber}</div>
                    <div className="col">{terminal?.terminalNumber || 'N/A'}</div>
                    <div className="col">{order.items?.length || 0} items</div>
                    <div className="col">₹{order.total?.toFixed(2)}</div>
                    <div className="col">
                      <span
                        className="status-pill"
                        style={{ backgroundColor: badge.bg, color: badge.color }}
                      >
                        {badge.text}
                      </span>
                    </div>
                    <div className="col">{getTimeAgo(order.createdAt)}</div>
                  </div>
                );
              })}

              {filteredOrders.length === 0 && (
                <div className="no-orders-row">
                  <p>No orders to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterPOSDashboard;
